// ─── Task Short-Link Route (shareable friendly URL) ───
// `/app/t/{slug}-{short}` — ADR 0012 (Phase 6.5 item 3). A task has no page of
// its own — this route is LOADER-ONLY and always redirects to the task's
// owning list's canonical friendly URL (`/app/l/$listRef`) with `?openTask=`
// set so the list route's existing TaskDetailPanel deep-link mechanism
// (Phase 6d, see routes/app/l/$listRef.tsx) opens it.
//
// Flow (identical parse → resolve as the other friendly routes, D2/D3):
//   1. parseEntityRef($taskRef) → { slug, short }. Malformed → notFound().
//   2. GET /resolve?type=task&short&slug via resolveEntity (404 → notFound(),
//      401 → smooth /login?returnTo redirect — both handled by resolveEntity).
//   3. Redirect (replace) to resolved.canonicalPath — the owning LIST's
//      friendly path, which the backend computes and returns — with
//      `?openTask=<task id>`.
//
// No slug self-heal here (unlike /app/l/$listRef and /app/s/$spaceRef): a
// task short-link always redirects onward to the list, so there is no
// canonical task URL to repaint in place — the list route's own loader
// handles its own canonical form.
//
// No component is registered (mirrors routes/app/spaces/$spaceId.tsx) — the
// loader always throws (redirect or notFound), so TanStack Router's default
// `<Outlet />` fallback is never reached in practice.

import { createFileRoute, notFound, redirect } from '@tanstack/react-router';
import { parseEntityRef } from '@a8-planner/shared/utils/entity-url';
import { resolveEntity } from '../../../lib/entity-resolve';

export const Route = createFileRoute('/app/t/$taskRef')({
  loader: async ({ params }): Promise<never> => {
    const parsed = parseEntityRef(params.taskRef);
    if (!parsed) {
      throw notFound();
    }

    const resolved = await resolveEntity('task', parsed.short, parsed.slug);

    throw redirect({
      to: resolved.canonicalPath as '/app',
      search: { openTask: resolved.id },
      replace: true,
    });
  },
});
